// src/components/QuickReplyPicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import { getQuickReplies } from '../services/quickReplyService';

/**
 * QuickReplyPicker - Componente para detectar "/" y mostrar sugerencias
 *
 * Se muestra como dropdown sobre el textarea cuando el usuario escribe "/"
 */
export default function QuickReplyPicker({ market, inputValue, onSelect, textareaRef }) {
  const [quickReplies, setQuickReplies] = useState([]);
  const [filteredReplies, setFilteredReplies] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const pickerRef = useRef(null);

  // Cargar respuestas rápidas al montar
  useEffect(() => {
    loadQuickReplies();
  }, [market]);

  const loadQuickReplies = async () => {
    try {
      const data = await getQuickReplies(market, null);
      setQuickReplies(data);
    } catch (err) {
      console.error('[QuickReplyPicker] Error loading quick replies:', err);
    }
  };

  // Detectar "/" en el input
  useEffect(() => {
    // Buscar el último "/" en el texto
    const lastSlashIndex = inputValue.lastIndexOf('/');

    if (lastSlashIndex === -1) {
      setShowPicker(false);
      return;
    }

    // Verificar que el "/" esté al inicio de una palabra
    // (inicio del texto, o después de espacio/newline)
    const beforeSlash = lastSlashIndex > 0 ? inputValue[lastSlashIndex - 1] : '\n';
    const isStartOfWord = beforeSlash === ' ' || beforeSlash === '\n' || lastSlashIndex === 0;

    if (!isStartOfWord) {
      setShowPicker(false);
      return;
    }

    // Obtener el texto después del "/"
    const afterSlash = inputValue.substring(lastSlashIndex + 1);

    // Si hay espacio o newline después del "/", cerrar el picker
    if (afterSlash.includes(' ') || afterSlash.includes('\n')) {
      setShowPicker(false);
      return;
    }

    // Filtrar respuestas rápidas
    const search = afterSlash.toLowerCase();
    setSearchTerm(search);

    const filtered = quickReplies.filter(qr =>
      qr.shortcut.toLowerCase().includes(search) ||
      qr.title.toLowerCase().includes(search)
    );

    setFilteredReplies(filtered);
    setShowPicker(filtered.length > 0);
    setSelectedIndex(0);
  }, [inputValue, quickReplies]);

  // Manejar teclas
  useEffect(() => {
    if (!showPicker) return;

    const handleKeyDown = (e) => {
      if (!showPicker) return;

      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredReplies.length - 1 ? prev + 1 : 0
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : filteredReplies.length - 1
          );
          break;

        case 'Enter':
          if (filteredReplies[selectedIndex]) {
            e.preventDefault();
            handleSelect(filteredReplies[selectedIndex]);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setShowPicker(false);
          break;

        default:
          break;
      }
    };

    // Agregar listener al textarea
    const textarea = textareaRef?.current;
    if (textarea) {
      textarea.addEventListener('keydown', handleKeyDown);
      return () => textarea.removeEventListener('keydown', handleKeyDown);
    }
  }, [showPicker, filteredReplies, selectedIndex, textareaRef]);

  const handleSelect = (quickReply) => {
    if (!onSelect) return;

    // Encontrar el último "/" en el input
    const lastSlashIndex = inputValue.lastIndexOf('/');

    // Reemplazar desde "/" hasta el final con el contenido
    const beforeSlash = inputValue.substring(0, lastSlashIndex);
    const newValue = beforeSlash + quickReply.content;

    onSelect(newValue);
    setShowPicker(false);
  };

  // Calcular posición del dropdown
  const getPickerStyle = () => {
    if (!textareaRef?.current) {
      return { bottom: '100%', left: 0 };
    }

    return {
      bottom: '100%',
      left: 0,
      marginBottom: '4px'
    };
  };

  if (!showPicker || filteredReplies.length === 0) {
    return null;
  }

  return (
    <div
      ref={pickerRef}
      style={{
        position: 'absolute',
        ...getPickerStyle(),
        width: '100%',
        maxHeight: '300px',
        overflowY: 'auto',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000
      }}
    >
      <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
          Respuestas rápidas {searchTerm && `(/${searchTerm})`}
        </span>
        <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>
          ↑↓ para navegar, Enter para seleccionar
        </span>
      </div>

      {filteredReplies.map((qr, index) => (
        <div
          key={qr.id}
          onClick={() => handleSelect(qr)}
          onMouseEnter={() => setSelectedIndex(index)}
          style={{
            padding: '10px 12px',
            cursor: 'pointer',
            backgroundColor: index === selectedIndex ? '#e3f2fd' : 'transparent',
            borderBottom: index < filteredReplies.length - 1 ? '1px solid #f0f0f0' : 'none',
            transition: 'background-color 0.1s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '11px',
                backgroundColor: '#e3f2fd',
                padding: '2px 6px',
                borderRadius: '4px',
                color: '#1976d2',
                fontWeight: 'bold'
              }}
            >
              /{qr.shortcut}
            </span>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>
              {qr.title}
            </span>
            {qr.usageCount > 0 && (
              <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>
                {qr.usageCount}x
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
            {qr.content.length > 80
              ? qr.content.substring(0, 80) + '...'
              : qr.content}
          </div>
        </div>
      ))}
    </div>
  );
}
